<template>
  <div class="erp-page page-wrap">
    <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>
    <v-card class="erp-filter-card mb-4">
      <div class="erp-page-header mb-3">
        <h1 class="erp-page-title">Reportes Contables</h1>
        <HelpHint text="Usá rango de fechas y tipo de reporte. Podés exportar CSV con el mismo filtro." />
      </div>
      <v-row dense>
        <v-col cols="12" md="2"><v-text-field v-model="filters.dateFrom" type="date" dense outlined label="Desde" /></v-col>
        <v-col cols="12" md="2"><v-text-field v-model="filters.dateTo" type="date" dense outlined label="Hasta" /></v-col>
        <v-col cols="12" md="4">
          <v-select v-model="reportType" :items="reportOptions" dense outlined label="Informe" item-text="label" item-value="value" />
        </v-col>
        <v-col v-if="reportType === 'account-ledger'" cols="12" md="4"><v-text-field v-model="filters.accountCode" dense outlined label="Código de cuenta contable" hint="Ej.: 410100 para Ventas" persistent-hint /></v-col>
        <v-col v-if="reportType === 'current-account-ledger'" cols="12" md="4"><v-autocomplete v-model="filters.currentAccountId" :items="customerOptions" :loading="customerSearchLoading" item-text="label" item-value="id" dense outlined clearable no-filter label="Cliente" hint="Buscá por nombre, código, CUIT o DNI" persistent-hint @update:search-input="searchReportCustomers" /></v-col>
      </v-row>
      <div class="d-flex align-center mt-2">
        <v-btn color="primary" @click="fetchReport"><v-icon left small>mdi-chart-box-outline</v-icon>Consultar</v-btn>
        <v-btn class="ml-2" outlined color="primary" @click="exportCsv"><v-icon left small>mdi-download-outline</v-icon>Exportar CSV</v-btn>
      </div>
    </v-card>

    <div v-if="summaryItems.length" class="balance-summary">
      <div v-for="item in summaryItems" :key="item.label" class="balance-summary-item" :class="item.tone || ''">
        <span>{{ item.label }}</span><strong>{{ item.value }}</strong>
      </div>
    </div>

    <v-card class="report-table-card">
      <v-data-table :headers="headers" :items="rows" :loading="loading" dense :footer-props="{ 'items-per-page-options': [10, 25, 50] }">
        <template v-slot:item.accountType="{ item }"><span class="account-type">{{ accountTypeLabel(item.accountType) }}</span></template>
        <template v-slot:item.totalDebit="{ item }"><span class="amount debit">{{ money(item.totalDebit) }}</span></template>
        <template v-slot:item.totalCredit="{ item }"><span class="amount credit">{{ money(item.totalCredit) }}</span></template>
        <template v-slot:item.balance="{ item }"><span class="amount" :class="Number(item.balance) < 0 ? 'credit' : 'debit'">{{ money(item.balance) }}</span></template>
        <template v-slot:item.debit="{ item }"><span class="amount debit">{{ money(item.debit) }}</span></template>
        <template v-slot:item.credit="{ item }"><span class="amount credit">{{ money(item.credit) }}</span></template>
        <template v-slot:item.netAmount="{ item }"><span class="amount">{{ money(item.netAmount) }}</span></template>
        <template v-slot:item.vatAmount="{ item }"><span class="amount">{{ money(item.vatAmount) }}</span></template>
        <template v-slot:item.perceptionAmount="{ item }"><span class="amount">{{ money(item.perceptionAmount) }}</span></template>
        <template v-slot:item.total="{ item }"><span class="amount">{{ money(item.total) }}</span></template>
        <template v-slot:item.openingBalance="{ item }"><span class="amount">{{ money(item.openingBalance) }}</span></template>
        <template v-slot:item.movement="{ item }"><span class="amount">{{ money(item.movement) }}</span></template>
        <template v-slot:item.closingBalance="{ item }"><span class="amount">{{ money(item.closingBalance) }}</span></template>
        <template v-slot:item.amount="{ item }"><span class="amount">{{ money(item.amount) }}</span></template>
        <template v-slot:item.standardCost="{ item }"><span class="amount">{{ money(item.standardCost) }}</span></template>
        <template v-slot:item.valuation="{ item }"><span class="amount">{{ money(item.valuation) }}</span></template>
        <template v-slot:item.section="{ item }"><span class="account-type">{{ item.section }}</span></template>
        <template v-slot:item.direction="{ item }"><span class="account-type">{{ directionLabel(item.direction) }}</span></template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { accountingService } from "@/services/accounting";
import { currentAccountsService } from "@/services/currentAccounts";
import HelpHint from "@/components/HelpHint.vue";

export default {
  name: "AccountingReportsView",
  components: { HelpHint },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      loading: false,
      error: "",
      reportType: "trial-balance",
      reportOptions: [
        { label: "Balance de sumas y saldos", value: "trial-balance" },
        { label: "Mayor de cuenta", value: "account-ledger" },
        { label: "Mayor cuenta corriente", value: "current-account-ledger" },
        { label: "IVA Ventas", value: "vat-sales" },
        { label: "IVA Compras", value: "vat-purchases" },
        { label: "Liquidación IVA (estimada)", value: "vat-settlement" },
        { label: "Stock Valorizado", value: "stock-valuation" },
        { label: "Balance general", value: "balance-sheet" },
        { label: "Estado de resultados", value: "income-statement" },
        { label: "Percepciones IIBB", value: "iibb-perceptions" },
        { label: "Retenciones impositivas", value: "tax-withholdings" },
        { label: "Dashboard Fiscal", value: "fiscal-dashboard" },
        { label: "Alertas Críticas", value: "fiscal-alerts" },
      ],
      filters: {
        dateFrom: today.slice(0, 8) + "01",
        dateTo: today,
        accountCode: "",
        currentAccountId: "",
      },
      rows: [],
      headers: [],
      summaryItems: [],
      customerOptions: [],
      customerSearchLoading: false,
    };
  },
  methods: {
    parseError(err) {
      return err?.response?.data?.error || err?.response?.data?.message || err?.message || "Error inesperado";
    },
    inferHeaders(rows) {
      if (!Array.isArray(rows) || !rows.length) return [];
      const labels = {
        section: "Grupo", code: "Código", name: "Cuenta / concepto", accountType: "Clase", entryDate: "Fecha", issueDate: "Fecha",
        docNumber: "Comprobante", invoiceType: "Tipo comprobante", cbteTipo: "Tipo AFIP", ptoVta: "Punto de venta", afipNumber: "Nº AFIP", afipStatus: "Estado AFIP", cae: "CAE", status: "Estado",
        warehouseCode: "Depósito", quantity: "Cantidad", standardCost: "Costo unitario", valuation: "Valor de stock",
        totalDebit: "Debe", totalCredit: "Haber", balance: "Saldo", openingBalance: "Saldo inicial", movement: "Movimiento", closingBalance: "Saldo final", amount: "Importe", netAmount: "Neto gravado", vatAmount: "IVA", perceptionAmount: "Percepciones", total: "Total",
        referenceType: "Origen", referenceId: "Comprobante", description: "Descripción", debit: "Debe", credit: "Haber", signedAmount: "Importe", openSignedAmount: "Pendiente", isReconciled: "Conciliado", direction: "Sentido", jurisdiction: "Jurisdicción", taxType: "Impuesto", withholdingDate: "Fecha", certificateNumber: "Certificado", taxableBase: "Base imponible", rate: "Alícuota", message: "Alerta", severity: "Nivel", qty: "Cantidad"
      };
      const numeric = new Set(["totalDebit", "totalCredit", "balance", "debit", "credit", "signedAmount", "openSignedAmount", "openingBalance", "movement", "closingBalance", "amount", "netAmount", "vatAmount", "perceptionAmount", "total", "standardCost", "valuation", "taxableBase"]);
      return Object.keys(rows[0]).filter((key) => !["accountId", "currentAccountId", "supplierAccountId", "productId", "id", "sourceId"].includes(key)).map((key) => ({
        text: labels[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, (letter) => letter.toUpperCase()),
        value: key,
        align: numeric.has(key) ? "end" : "start"
      }));
    },
    money(value) {
      return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));
    },
    accountTypeLabel(value) {
      const types = { asset_cash: "Activo · Caja", asset_inventory: "Activo · Inventario", liability_tax: "Pasivo · Impuestos", income: "Resultado · Ventas", expense: "Resultado · Costos", asset: "Activo", liability: "Pasivo", equity: "Patrimonio" };
      return types[value] || String(value || "—");
    },
    async searchReportCustomers(query) {
      const text = String(query || "").trim();
      if (text.length < 2) { this.customerOptions = []; return; }
      this.customerSearchLoading = true;
      try {
        const response = await currentAccountsService.list({ q: text, limit: 20, accountType: "customer" });
        const rows = response?.data?.data || response?.data || [];
        this.customerOptions = (Array.isArray(rows) ? rows : []).map((row) => ({ id: row.id, label: [row.code, row.name, row.taxId].filter(Boolean).join(" · ") }));
      } catch (error) { this.customerOptions = []; }
      finally { this.customerSearchLoading = false; }
    },
    directionLabel(value) {
      return value === "collected" ? "Percibida" : value === "suffered" ? "Sufrida" : String(value || "—");
    },
    normalizeReport(payload) {
      const moneySummary = (label, value, tone = "") => ({ label, value: this.money(value), tone });
      const source = Array.isArray(payload) ? { rows: payload } : (payload || {});
      if (this.reportType === "trial-balance") {
        const debit = (source.rows || []).reduce((sum, row) => sum + Number(row.totalDebit || 0), 0);
        const credit = (source.rows || []).reduce((sum, row) => sum + Number(row.totalCredit || 0), 0);
        const difference = debit - credit;
        return { rows: source.rows || [], summary: [moneySummary("Debe", debit), moneySummary("Haber", credit), moneySummary(Math.abs(difference) < .01 ? "Balanceado" : "Diferencia", Math.abs(difference), Math.abs(difference) < .01 ? "is-balanced" : "is-unbalanced")] };
      }
      if (Array.isArray(payload)) return { rows: payload, summary: [] };
      if (this.reportType === "balance-sheet") return { rows: [...(source.assets || []).map((r) => ({ section: "Activo", ...r })), ...(source.liabilities || []).map((r) => ({ section: "Pasivo", ...r }))], summary: [moneySummary("Activo", source.totals?.assets), moneySummary("Pasivo", source.totals?.liabilities)] };
      if (this.reportType === "income-statement") return { rows: [...(source.income || []).map((r) => ({ section: "Ingresos", ...r })), ...(source.expenses || []).map((r) => ({ section: "Costos y gastos", ...r }))], summary: [moneySummary("Ingresos", source.totals?.income), moneySummary("Costos y gastos", source.totals?.expenses), moneySummary("Resultado", source.result, Number(source.result || 0) >= 0 ? "is-balanced" : "is-unbalanced")] };
      if (["vat-sales", "vat-purchases"].includes(this.reportType)) return { rows: source.rows || [], summary: [moneySummary("Neto gravado", source.totals?.netAmount), moneySummary("IVA", source.totals?.vatAmount), moneySummary("Percepciones", source.totals?.perceptionAmount), moneySummary("Total", source.totals?.total)] };
      if (this.reportType === "vat-settlement") {
        const balance = Number(source.totals?.technicalBalance || 0);
        return {
          rows: source.rows || [],
          summary: [
            moneySummary("IVA débito · ventas", source.totals?.vatDebit),
            moneySummary("IVA crédito · compras", source.totals?.vatCredit),
            moneySummary(balance > 0 ? "Saldo técnico estimado a pagar" : balance < 0 ? "Saldo técnico a favor" : "Saldo técnico", Math.abs(balance), balance > 0 ? "is-unbalanced" : "is-balanced"),
            moneySummary("Percepciones de ventas (informativo)", source.totals?.salesPerceptions),
            moneySummary("Percepciones de compras (informativo)", source.totals?.purchasePerceptions)
          ]
        };
      }
      if (this.reportType === "iibb-perceptions") return { rows: source.rows || [], summary: [moneySummary("Percibido", source.totals?.collected), moneySummary("Sufrido", source.totals?.suffered), moneySummary("Neto", source.totals?.net)] };
      if (this.reportType === "tax-withholdings") return { rows: source.rows || [], summary: [moneySummary("Retenciones practicadas", source.totals?.collected), moneySummary("Retenciones sufridas", source.totals?.suffered)] };
      if (this.reportType === "stock-valuation") return { rows: source.rows || [], summary: [moneySummary("Valor total de stock", source.total)] };
      if (this.reportType === "fiscal-dashboard") {
        const names = { missingCae: "Facturas sin CAE", failedAfip: "Comprobantes AFIP fallidos", duplicateAfip: "Duplicados AFIP", unpostedInvoices: "Facturas sin asiento", unbalancedEntries: "Asientos desbalanceados", stockWithoutRef: "Stock sin documento", purchasesPendingInvoice: "Compras sin factura" };
        const rows = Object.keys(names).map((key) => ({ message: names[key], qty: Number(source[key] || 0) }));
        return { rows, summary: rows.map((row) => ({ label: row.message, value: String(row.qty), tone: row.qty ? "is-unbalanced" : "is-balanced" })) };
      }
      return { rows: source.rows || (source && typeof source === "object" ? [source] : []), summary: [] };
    },
    buildParams() {
      const base = { dateFrom: this.filters.dateFrom, dateTo: this.filters.dateTo, limit: 500, offset: 0 };
      if (this.reportType === "account-ledger" && this.filters.accountCode) base.accountCode = this.filters.accountCode;
      if (this.reportType === "current-account-ledger" && this.filters.currentAccountId) base.currentAccountId = this.filters.currentAccountId;
      return base;
    },
    async fetchReport() {
      this.error = "";
      if (this.reportType === "account-ledger" && !this.filters.accountCode) { this.error = "Ingresá el código de la cuenta contable que querés consultar."; return; }
      if (this.reportType === "current-account-ledger" && !this.filters.currentAccountId) { this.error = "Seleccioná una cuenta corriente de cliente para consultar su mayor."; return; }
      this.loading = true;
      this.error = "";
      try {
        const params = this.buildParams();
        const requests = {
          "trial-balance": () => accountingService.getTrialBalance(params),
          "account-ledger": () => accountingService.getAccountLedger(params),
          "current-account-ledger": () => accountingService.getCurrentAccountLedger(params),
          "vat-sales": () => accountingService.getVatSales(params),
          "vat-purchases": () => accountingService.getVatPurchases(params),
          "vat-settlement": () => accountingService.getVatSettlement(params),
          "balance-sheet": () => accountingService.getBalanceSheet(params),
          "income-statement": () => accountingService.getIncomeStatement(params),
          "iibb-perceptions": () => accountingService.getIibbPerceptions(params),
          "tax-withholdings": () => accountingService.getTaxWithholdings(params),
          "stock-valuation": () => accountingService.getStockValuation(),
          "fiscal-dashboard": () => accountingService.getFiscalDashboard(),
          "fiscal-alerts": () => accountingService.getFiscalAlerts(),
        };
        const request = requests[this.reportType];
        if (!request) throw new Error("Reporte no disponible");
        const response = await request();
        const result = this.normalizeReport(response?.data?.data);
        this.rows = result.rows;
        this.summaryItems = result.summary;
        this.headers = this.inferHeaders(result.rows);
      } catch (err) {
        this.error = this.parseError(err);
      } finally {
        this.loading = false;
      }
    },
    async exportCsv() {
      this.error = "";
      try {
        const params = { ...this.buildParams(), format: "csv" };
        let response;
        if (this.reportType === "trial-balance") response = await accountingService.getTrialBalance(params);
        if (this.reportType === "account-ledger") response = await accountingService.getAccountLedger(params);
        if (this.reportType === "current-account-ledger") response = await accountingService.getCurrentAccountLedger(params);
        if (!["trial-balance", "account-ledger", "current-account-ledger"].includes(this.reportType)) {
          await this.fetchReport();
          const csv = [this.headers.map(h => h.text).join(",")].concat(this.rows.map(row => this.headers.map(h => JSON.stringify(row[h.value] ?? "")).join(","))).join("\n");
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `accounting-${this.reportType}-${Date.now()}.csv`;
          a.click();
          URL.revokeObjectURL(url);
          return;
        }
        const csv = typeof response?.data === "string" ? response.data : "";
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `accounting-${this.reportType}-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        this.error = this.parseError(err);
      }
    },
  },
};
</script>


<style scoped>
.page-wrap { display: flex; flex-direction: column; gap: 14px; max-width: 1240px; margin: 0 auto; }
.balance-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.balance-summary-item { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; box-shadow: 0 1px 3px rgba(15,23,42,.06); }
.balance-summary-item span { display: block; color: #64748b; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
.balance-summary-item strong { display: block; color: #172554; font-size: 21px; margin-top: 5px; }
.balance-summary-item.is-balanced { border-color: #86efac; background: #f0fdf4; }
.balance-summary-item.is-balanced strong { color: #166534; }
.balance-summary-item.is-unbalanced { border-color: #fecaca; background: #fef2f2; }
.balance-summary-item.is-unbalanced strong { color: #b91c1c; }
.report-table-card { overflow: hidden; }
.amount { font-variant-numeric: tabular-nums; font-weight: 700; white-space: nowrap; }
.amount.debit { color: #166534; }
.amount.credit { color: #9f1239; }
.account-type { color: #475569; font-size: 12px; }
@media (max-width: 700px) { .balance-summary { grid-template-columns: 1fr; } }
</style>
